/**
 * BodySnapshot — fast-paint cache for the recipe editor body.
 *
 * Problem: on warm opens the recipe body is invisible for 500–1000 ms while
 * the y-sweet WebSocket handshake + server re-hydrate runs. The RPC cache
 * (packages/client-core/src/cache) already makes `recipe.get` fast; the
 * bottleneck is the y-doc sync, not the network metadata.
 *
 * Solution: persist a snapshot of the y-doc `blocks` map every time the user
 * pauses typing (debounced 500 ms) and on every explicit save. On recipe open,
 * read the snapshot synchronously from L1 (or async from IDB) and paint it as
 * a non-editable overlay behind the TipTap editor. The overlay is removed the
 * instant `onSync(true)` fires and y-doc content replaces it.
 *
 * Storage layer:
 *   Uses the `storeGet` / `storeSet` primitives from `@proof/client-core/cache`
 *   which wrap IndexedDB via `idb-keyval` with an in-memory L1 mirror. The
 *   snapshot key namespace (`proof.ydoc-snapshot.v1.*`) is separate from the
 *   RPC cache namespace (`proof.qcache.v1.*`) so prefix-based invalidation
 *   cannot accidentally clear snapshots.
 *
 * Key format:
 *   `proof.ydoc-snapshot.v1.<branchUuid>`
 *
 * Max age: 24 h. Stale snapshots on warm open are OK (they paint first and are
 * replaced by y-doc sync), but we evict them to avoid unbounded growth. Because
 * the y-doc is always the authority, a stale snapshot never corrupts data.
 *
 * Architecture refs:
 *   architecture.md §4.4 — y-doc schema (flat Y.Map<Y.Map> "blocks")
 *   spike 0.C — determinism property (block shape contract)
 *   apps/web/src/features/editor/BlocksBinding.ts — FlatBlock type
 *   packages/client-core/src/cache/store.ts — IDB layer we reuse
 */

import { storeGet, storeSet, storeDelByPrefix } from "@proof/client-core/cache";
import type { FlatBlock } from "./BlocksBinding.js";

// ── Key schema ────────────────────────────────────────────────────────────────

const SNAPSHOT_PREFIX = "proof.ydoc-snapshot.v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 h

export function snapshotKey(branchUuid: string): string {
  return `${SNAPSHOT_PREFIX}.${branchUuid}`;
}

// ── Entry shape ───────────────────────────────────────────────────────────────

export interface BodySnapshotEntry {
  blocks: FlatBlock[];
  writtenAt: number;
}

// ── L1 in-memory mirror ───────────────────────────────────────────────────────
//
// A separate L1 Map from the RPC cache L1. This is intentional: snapshot reads
// are on the hot render path; keeping them in a dedicated map avoids coupling
// to the RPC cache's eviction and key-scan logic.

const L1 = new Map<string, BodySnapshotEntry>();

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * Synchronous L1 read. Returns the entry if present AND younger than MAX_AGE_MS.
 * Returns null on any miss. Call this first — it is sub-millisecond and can be
 * used during React render or in a layout effect without async overhead.
 */
export function getSnapshotSync(branchUuid: string): BodySnapshotEntry | null {
  const entry = L1.get(snapshotKey(branchUuid));
  if (!entry) return null;
  if (Date.now() - entry.writtenAt > MAX_AGE_MS) {
    L1.delete(snapshotKey(branchUuid));
    return null;
  }
  return entry;
}

/**
 * Async read: L1 first, then the shared IDB store. Returns the entry if found
 * AND fresh. Best-effort — returns null on any error.
 */
export async function getSnapshot(branchUuid: string): Promise<BodySnapshotEntry | null> {
  const l1 = getSnapshotSync(branchUuid);
  if (l1) return l1;

  const key = snapshotKey(branchUuid);
  try {
    // storeGet wraps IDB in CacheEntry<T> = { data: T; storedAt: number }.
    // Our BodySnapshotEntry is stored as the `data` field.
    const cacheEntry = await storeGet<BodySnapshotEntry>(key);
    if (!cacheEntry) return null;
    const entry = cacheEntry.data;
    if (!isValidEntry(entry)) return null;
    if (Date.now() - entry.writtenAt > MAX_AGE_MS) return null;
    // Warm L1 for the next navigation.
    L1.set(key, entry);
    return entry;
  } catch (err) {
    console.warn("[proof.snapshot] IDB get failed", { branchUuid, err });
    return null;
  }
}

// ── Write ─────────────────────────────────────────────────────────────────────

/**
 * Write a block snapshot. Writes to L1 synchronously and fires the IDB write
 * async (fire-and-forget from the caller's perspective).
 *
 * Call this from the debounced observer in Workspace after the y-doc settles.
 */
export async function setSnapshot(branchUuid: string, blocks: FlatBlock[]): Promise<void> {
  const entry: BodySnapshotEntry = { blocks, writtenAt: Date.now() };
  const key = snapshotKey(branchUuid);
  // L1 write first (synchronous) so the next getSnapshotSync hits immediately.
  L1.set(key, entry);
  // storeSet wraps the entry in CacheEntry and writes to IDB.
  await storeSet(key, entry);
}

// ── Identity gate (async-fallback #335 defense) ───────────────────────────────

/**
 * Gate an async `getSnapshot` result against the identity that is active at
 * resolve time. Returns the entry's blocks ONLY when the branch the entry was
 * resolved for still matches the branch currently keying the editor subtree.
 *
 * This is the overlay-layer defense against the #335 wrong-body-on-switch class
 * (PRs #335 / #350 / #351): an async IDB read fired for variation A that
 * resolves after the user has switched to variation B must never paint A's body
 * under B's identity. The synchronous `getSnapshotSync` path cannot exhibit the
 * bug (it resolves in the same tick as the identity read), but the async
 * fallback can — so every async paint routes through this gate.
 *
 * @param resolvedForBranch  the branchUuid the getSnapshot call was made for
 * @param currentBranch      the branchUuid keying the editor subtree right now
 * @param entry              the resolved snapshot entry (or null on a miss)
 */
export function gateAsyncSnapshot(
  resolvedForBranch: string,
  currentBranch: string | undefined,
  entry: BodySnapshotEntry | null,
): FlatBlock[] | null {
  if (entry === null) return null;
  if (currentBranch === undefined || resolvedForBranch !== currentBranch) return null;
  return entry.blocks;
}

// ── Render-time identity gate (sync/state-path #335 defense) ───────────────────

/**
 * Tagged reader-hook state: the resolved blocks PLUS the branch identity they
 * were resolved for. The hook stores this (not a bare `FlatBlock[]`) so a
 * render-time gate can drop a value that belongs to a now-stale branch.
 */
export interface TaggedSnapshot {
  forBranch: string;
  blocks: FlatBlock[];
}

/**
 * Render-time identity gate for the reader hook's tagged state. Returns the
 * blocks ONLY when the branch they were resolved for matches the branch
 * currently keying the editor.
 *
 * This closes the #335 wrong-body-on-switch class on the SYNCHRONOUS/state path
 * (the async path is covered by `gateAsyncSnapshot`). React state updates run
 * AFTER paint, so the first commit after a B→A switch carries `currentBranch=A`
 * while the hook state still holds `{ forBranch: B, ... }` — a bare state read
 * would paint B's body under A's identity for ≥1 frame (Workspace is unkeyed, so
 * the keyed editor wrapper does not save you; the stale value arrives via
 * state). The tag mismatch here drops it BY CONSTRUCTION, so the overlay paints
 * nothing rather than the wrong body.
 */
export function selectSnapshotBlocks(
  state: TaggedSnapshot | null,
  currentBranch: string | undefined,
): FlatBlock[] | null {
  if (state === null || currentBranch === undefined) return null;
  if (state.forBranch !== currentBranch) return null;
  return state.blocks;
}

/**
 * Render-time overlay-blocks resolver — `selectSnapshotBlocks` PLUS a synchronous
 * L1 fallback for the recipe/variation-switch WINDOW (SF-1, perf recipe-switch).
 *
 * On a switch the reader hook's tagged `state` still holds the PREVIOUS branch's
 * blocks (React state updates land after paint), so `selectSnapshotBlocks` drops
 * it → null, and the hook's effect only resolves the NEW branch's snapshot AFTER
 * paint. That left the fast-paint overlay uncovered for the first post-switch
 * frame — a blank-editor beat. Falling back to a synchronous L1 read for the
 * CURRENT branch closes it BY CONSTRUCTION:
 *   - REVISIT (the branch has an L1 snapshot) → its blocks paint on that first
 *     frame, so the overlay covers the window with the NEW identity's body.
 *   - FIRST-EVER open (no snapshot) → null → honest empty state; we never
 *     fabricate content.
 * `getSnapshotSync` is sub-millisecond and reached only during the window
 * (steady state returns via the gate). The result is implicitly tagged for
 * `currentBranch` because the read key IS `currentBranch`, so it can never paint
 * a stale branch's body.
 */
export function resolveOverlaySnapshotBlocks(
  state: TaggedSnapshot | null,
  currentBranch: string | undefined,
): FlatBlock[] | null {
  const gated = selectSnapshotBlocks(state, currentBranch);
  if (gated !== null) return gated;
  if (currentBranch === undefined) return null;
  const sync = getSnapshotSync(currentBranch);
  return sync ? sync.blocks : null;
}

// ── Dissolve decision ─────────────────────────────────────────────────────────

/**
 * Whether the fast-paint overlay should be visible. Visible only while there is
 * a snapshot to show AND the real body is not yet available.
 *
 * The real body is "available" when EITHER:
 *   - `recipeContent` (the identity-gated draft body) is non-empty — the normal
 *     path: the live editor now shows the content, so the overlay is redundant;
 *     OR
 *   - `bodyHydrated` is true — the doc has completed its WS sync handshake
 *     (`provider.synced`). This is the TERMINAL signal for the synced-but-EMPTY
 *     body case (delete-all locally or on another device): an empty sync carries
 *     no structs, so NO content-derived signal ever fires (neither the doc
 *     `update` event nor BlocksBinding's `observeDeep`), and `recipeContent`
 *     stays "" forever. Without this the deleted-body snapshot would stay painted
 *     for the whole session, an opaque layer hiding the caret. `bodyHydrated`
 *     must be a real handshake signal, NOT `syncState`: the provider's status
 *     flips to "connected" (→ syncState "saved") BEFORE syncStep2, so gating on
 *     syncState would dissolve pre-hydration in the normal path and reintroduce
 *     the blank flash this feature exists to kill.
 *
 * Forced off when there is no live doc (`hasYDoc` false — the #351 no-docKey
 * path, which would otherwise leave the overlay painted forever).
 */
export function isBodySnapshotOverlayVisible(args: {
  hasYDoc: boolean;
  snapshotBlocks: FlatBlock[] | null;
  recipeContent: unknown;
  bodyHydrated: boolean;
}): boolean {
  const { hasYDoc, snapshotBlocks, recipeContent, bodyHydrated } = args;
  if (!hasYDoc) return false;
  if (snapshotBlocks === null || snapshotBlocks.length === 0) return false;
  if (bodyHydrated) return false;
  if (typeof recipeContent === "string" && recipeContent.length > 0) return false;
  return true;
}

// ── Purge (sign-out privacy) ──────────────────────────────────────────────────

/**
 * Drop every body snapshot (L1 + IDB). Called on sign-out so the next user on a
 * shared device cannot see the previous user's recipe body.
 *
 * The snapshot namespace (`proof.ydoc-snapshot.v1.*`) sits outside both the RPC
 * cache prefix and the `proof-yjs` DB, so neither `invalidateAll(userId)` nor
 * `purgeWebYjsCache()` clears it — this explicit purge is the only path that
 * does. Best-effort on the IDB side (`storeDelByPrefix` swallows + warns on IDB
 * failure), but the synchronous L1 clear always runs first so an in-memory
 * snapshot never survives sign-out even if IDB is unavailable.
 */
export async function purgeAllSnapshots(): Promise<void> {
  // Synchronous L1 clear first — guarantees no in-memory snapshot outlives
  // sign-out regardless of the IDB outcome below.
  L1.clear();
  await storeDelByPrefix(SNAPSHOT_PREFIX);
}

// ── Shape guard ───────────────────────────────────────────────────────────────

function isValidEntry(raw: unknown): raw is BodySnapshotEntry {
  if (typeof raw !== "object" || raw === null) return false;
  const r = raw as Record<string, unknown>;
  if (typeof r["writtenAt"] !== "number") return false;
  if (!Array.isArray(r["blocks"])) return false;
  return true;
}

// ── Test helpers ──────────────────────────────────────────────────────────────

/** Drop L1 without touching IDB. For unit tests only. */
export function __resetL1ForTest(): void {
  L1.clear();
}
